import {
  SignatureEventSchema,
  type DepositGate,
  type SignContextResponse,
  type SignatureEvent,
  type SignedDocumentRef,
  type StartDateSelection,
} from '@rheora/enrollment-contract';

import { checkSignatureImagePng } from './signature-image.service';

/**
 * Consent, click-wrap sign & instant-accept form logic (E-SF06) — kept OUT of the
 * `.tsx` per CLAUDE.md. Pure: no fetches, no `Date.now()` unless injected, no
 * business-day math (start-date + rescission dates are computed SERVER-SIDE with a
 * real Texas holiday set and only rendered here).
 *
 * ── ADV-2 SCOPED EXCEPTION (recorded, same as E-SF05) ──────────────────────
 * `@fluxora/forms` (RHF + Zod) is a non-functional stub in this workspace, so the
 * sign surface follows the E-SF02/03/05 precedent: `useState` form + this-service
 * Zod validation, with the shared contract schema as the final arbiter.
 *
 * ── NOTHING IS PRE-ACCEPTED ────────────────────────────────────────────────
 * Every consent starts false and the TCPA SMS opt-in starts false. The click-wrap
 * is an explicit affirmative act; this service never defaults an acceptance true.
 */

export interface SignForm {
  /** SWITCH only: pick the first-available date or a chosen one. */
  readonly startMode: 'FIRST_AVAILABLE' | 'CHOSEN';
  /** The chosen date (SWITCH+CHOSEN, or the MOVE_IN date). ISO YYYY-MM-DD. */
  readonly chosenDate: string;
  /** MOVE_IN + AMS only: request a same-day start. */
  readonly requestSameDay: boolean;
  /** Click-wrap: accepted the Electricity Facts Label. */
  readonly eflAccepted: boolean;
  /** Click-wrap: accepted the Terms of Service. */
  readonly tosAccepted: boolean;
  /** Click-wrap: accepted Your Rights as a Customer. */
  readonly yracAccepted: boolean;
  /** Explicit TCPA SMS opt-in — starts false, the customer must check it. */
  readonly tcpaSmsConsent: boolean;
  /**
   * The drawn mark (E-SF17), as the PNG data URI the pad produced.
   *
   * ⚠️ REQUIRED SINCE THE COMPULSORY-SIGNATURE CHANGE — the doc-comment that used
   * to open "OPTIONAL drawn mark" is deliberately gone, and `undefined` is now a
   * validation failure rather than the normal case. See `SignGateInput.signatureDrawn`
   * for the reversal, its rationale and its accessibility cost.
   *
   * ⚠️ STILL CORROBORATING, NOT OPERATIVE. Requiring it does not promote it. The
   * typed name captured at the credit step remains the operative ESIGN mark and is
   * enforced at the database level by `signature_event_signer_name_present_check`;
   * the three acknowledgements remain mandatory. This is an ADDED requirement, and
   * nothing it touches was relaxed to make room for it.
   */
  readonly signatureImagePng: string | undefined;
  /**
   * i18n key of the reason a drawn mark was REFUSED client-side, or undefined.
   *
   * ⚠️ THIS IS WHY IT IS FORM STATE AND NOT A TRANSIENT TOAST. A refused mark
   * BLOCKS the submit (see `buildSignatureEvent`). Dropping it silently and
   * signing typed-only would hand a customer who believes they drew their
   * signature a contract without one — the same "appears to work" failure the
   * BFF-forwarding half of this epic exists to prevent. Clearing the pad clears
   * this and signing proceeds, typed-only, exactly as it always could.
   */
  readonly signatureRejectedKey: string | undefined;
}

/**
 * Initial form state for a given sign context. Start date defaults to the
 * server's `firstAvailableDate`; every consent + the SMS opt-in default to false.
 */
export function emptySignForm(context: SignContextResponse): SignForm {
  return {
    startMode: 'FIRST_AVAILABLE',
    chosenDate: context.startDate.firstAvailableDate,
    requestSameDay: false,
    eflAccepted: false,
    tosAccepted: false,
    yracAccepted: false,
    tcpaSmsConsent: false,
    signatureImagePng: undefined,
    signatureRejectedKey: undefined,
  };
}

/**
 * Attach (or clear) a drawn mark, running the client-side bounds.
 *
 * Kept here rather than in the page so the "refused marks are never silently
 * dropped" rule lives with the rest of the sign logic and is unit-testable.
 * `null` (cleared pad) resets BOTH the mark and any refusal.
 */
export function withDrawnSignature(form: SignForm, dataUri: string | null): SignForm {
  if (dataUri === null) {
    return { ...form, signatureImagePng: undefined, signatureRejectedKey: undefined };
  }
  const checked = checkSignatureImagePng(dataUri);
  return checked.ok
    ? { ...form, signatureImagePng: checked.value, signatureRejectedKey: undefined }
    : { ...form, signatureImagePng: undefined, signatureRejectedKey: checked.messageKey };
}

export type SignFormErrorKey = 'startDate' | 'efl' | 'tos' | 'yrac' | 'signature';
export type SignFormErrors = Partial<Record<SignFormErrorKey, string>>;

/**
 * True when a REQUIRED deposit is settled enough for accept. Reads the
 * SERVER-AUTHORITATIVE `satisfied` flag — it never recomputes settlement from
 * `state` (whether a low-income installment counts is a §25.478 decision owned
 * server-side; see the contract DepositGate note). No deposit required ⇒ satisfied.
 */
export function isDepositGateSatisfied(gate: DepositGate): boolean {
  if (!gate.required) return true;
  return gate.satisfied;
}

/** True when the deposit was satisfied via an ATTESTED (not verified) waiver. */
export function isWaiverSettled(gate: DepositGate): boolean {
  return (
    gate.required &&
    gate.satisfied &&
    (gate.state === 'DEPOSIT_WAIVED' || gate.state === 'DEPOSIT_INSTALLMENT_SCHEDULED')
  );
}

/** All three disclosures click-wrapped. TCPA is deliberately NOT part of this. */
export function isConsentComplete(form: SignForm): boolean {
  return form.eflAccepted && form.tosAccepted && form.yracAccepted;
}

/**
 * True when a drawn mark is present AND was not refused by the client-side bounds.
 *
 * Both halves matter. `withDrawnSignature` clears `signatureImagePng` when it
 * refuses a drawing, so a refused mark already reads as absent here — but checking
 * the refusal explicitly means a future change that keeps the bytes around cannot
 * turn "we refused this drawing" into "the gate is satisfied" by accident.
 */
export function hasDrawnSignature(form: SignForm): boolean {
  return form.signatureImagePng !== undefined && form.signatureRejectedKey === undefined;
}

/** Build the start-date selection for the SignatureEvent from the form + context. */
/**
 * ⚠️ THIS FIELD IS INERT, AND THE CUSTOMER IS NO LONGER ASKED FOR IT HERE.
 *
 * The supply start date is chosen on the ESTIMATE step now (2026-09-18) and
 * travels to Quotation as the DRAFT quote's `requestedStartDate`, which is what
 * the EFL, the contract term and the contract PDF are all built from. That is
 * the only route by which the customer's date can reach the contract: once the
 * draft exists its price and EFL are fixed, and the accept path never re-prices.
 *
 * `SignatureEvent.startDate` is a required field of the shared contract whose
 * BFF disposition is `NOT_FORWARDED_KNOWN_GAP` — the accept hop has no field for
 * it and nothing reads it. So it is filled from the server's own window rather
 * than from a second question to the customer, which would have been a question
 * whose answer changed nothing while appearing to change the start of supply.
 *
 * If a future epic makes accept carry this field, DO NOT simply start trusting
 * it — a date honoured at accept has to re-price, which is the whole reason it
 * was moved earlier.
 */
function buildStartDateSelection(context: SignContextResponse): StartDateSelection {
  if (context.enrollmentType === 'SWITCH') {
    return { enrollmentType: 'SWITCH', mode: 'FIRST_AVAILABLE' };
  }
  return {
    enrollmentType: 'MOVE_IN',
    date: context.startDate.firstAvailableDate,
    // Never requested here any more; the estimate step owns the choice.
    requestSameDay: false,
  };
}

/** The three accepted disclosure versions, from the context, for ESIGN association. */
function acceptedDocuments(context: SignContextResponse): SignedDocumentRef[] {
  return [
    { kind: 'EFL', version: context.disclosures.eflVersion },
    { kind: 'TOS', version: context.disclosures.tosVersion },
    { kind: 'YRAC', version: context.disclosures.yracVersion },
  ];
}

export interface BuildSignatureEventArgs {
  readonly form: SignForm;
  readonly context: SignContextResponse;
  readonly candidateId: string;
  readonly planId: string;
  /** Injected for deterministic tests; the client-attested acceptance instant. */
  readonly now: Date;
}

export interface SignatureEventValidation {
  readonly ok: boolean;
  readonly errors: SignFormErrors;
  readonly value: SignatureEvent | undefined;
}

/**
 * Validate the sign form and build a `SignatureEvent`. Field errors are i18n KEYS
 * (resolved by the page). A chosen date must fall within the server's
 * [earliest, latest] window (a lexicographic ISO-date bound — never date math).
 * The shared contract schema is the final arbiter, so the FE and BFF agree on a
 * well-formed signature event.
 */
export function buildSignatureEvent(args: BuildSignatureEventArgs): SignatureEventValidation {
  const { form, context, candidateId, planId, now } = args;
  const errors: SignFormErrors = {};

  // No start-date validation here any more: this step does not ask for one.
  // The date was chosen (and bounds-checked, by both the estimate step and the
  // BFF) before the DRAFT quote was created — see `buildStartDateSelection`.

  if (!form.eflAccepted) errors.efl = 'shop.sign.consent.error.efl';
  if (!form.tosAccepted) errors.tos = 'shop.sign.consent.error.tos';
  if (!form.yracAccepted) errors.yrac = 'shop.sign.consent.error.yrac';

  // ── THE DRAWN MARK, IN TWO DISTINCT FAILURES ──────────────────────────────
  //
  // A REFUSED mark is reported first, and the order is load-bearing: "that drawing
  // couldn't be read, clear it and try again" is actionable, while "please draw
  // your signature" told to someone who has just drawn one reads as the page
  // ignoring them. A refused mark leaves `signatureImagePng` undefined, so without
  // this precedence the specific message would be masked by the generic one.
  //
  // The second branch is the compulsory-signature requirement itself. It duplicates
  // `signBlockedReasonKey`'s gate ON PURPOSE: that one disables the button, this one
  // is what a submit reaching the service is measured against. A validation that
  // exists only as a disabled attribute is one DOM edit away from producing an
  // unsigned contract.
  if (form.signatureRejectedKey !== undefined) {
    errors.signature = form.signatureRejectedKey;
  } else if (form.signatureImagePng === undefined) {
    errors.signature = 'shop.sign.signature.error.required';
  }

  if (Object.keys(errors).length > 0) return { ok: false, errors, value: undefined };

  const candidate: SignatureEvent = {
    candidateId,
    planId,
    startDate: buildStartDateSelection(context),
    consents: { efl: true, tos: true, yrac: true },
    tcpaSmsConsent: form.tcpaSmsConsent,
    // ⚠️ THE CONDITIONAL SPREAD STAYS, even though the validation above now makes
    // the undefined branch unreachable from this storefront. It is not dead code:
    // a typed-only signature must carry NO key at all — not `null`, not
    // `undefined` — because E38 pins the typed-only contract PDF as byte-identical
    // to its pre-E-SF17 output, and the CRM still produces typed-only signatures on
    // the same contract. Collapsing this to an unconditional key would make THIS
    // file the reason that guarantee broke the next time the requirement moves.
    ...(form.signatureImagePng === undefined
      ? {}
      : { signatureImagePng: form.signatureImagePng }),
    clickwrap: {
      agreed: true,
      acceptedAt: now.toISOString(),
      documents: acceptedDocuments(context),
    },
  };

  // Belt-and-braces: the shared contract schema is the final arbiter.
  const parsed = SignatureEventSchema.safeParse(candidate);
  if (!parsed.success) {
    // Attribute the refusal to the field that caused it. Blaming the start date
    // for an oversized drawing sends the customer to fix something that is not
    // wrong, and leaves the thing that IS wrong unmentioned.
    const blamedSignature = parsed.error.issues.some(
      (issue) => issue.path[0] === 'signatureImagePng',
    );
    return blamedSignature
      ? { ok: false, errors: { signature: 'shop.sign.signature.error.tooLarge' }, value: undefined }
      : {
          ok: false,
          errors: { startDate: 'shop.sign.startDate.error.required' },
          value: undefined,
        };
  }
  return { ok: true, errors: {}, value: parsed.data };
}
